import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getCreationUrl } from 'automation-ui-canvas/utils/urls';
import CloneWorkflowModal from 'automation-ui-management/components/modals/CloneWorkflowModal';
import ExportWorkflowModal from 'automation-ui-canvas/components/modals/ExportWorkflowModal';
import EditWorkflowNameAndDescriptionModal from 'automation-ui-management/components/WorkflowNameWithDescription/EditWorkflowNameAndDescriptionModal';
import { useFeaturePermission } from 'automation-ui-permissions/feature-permissions/hooks/featurePermissionHooks';
import { EDIT_ACTIONS } from 'automation-ui-permissions/feature-permissions/constants/WorkflowManagementFeatures';
import { NEW_ACTION } from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { useLocation } from 'react-router-dom';
import { AUTOMATION_PLATFORM_FLOW_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import OpenAssignTeamPanelButton from 'automation-ui-management/components/OpenAssignTeamPanelButton';
import { WORKFLOWS_APP } from 'automation-ui-source-apps/constants/SourceApps';
import OpenBusinessUnitModalButton from 'automation-ui-management/components/OpenBusinessUnitModalButton';
import AssignBusinessUnitModal from 'automation-ui-management/components/modals/AssignBusinessUnitModal';
import { useDataFetchingClient, useQuery } from 'data-fetching-client';
import { CRM_OBJECT_SEARCH_FIELD_NAME, CRM_OBJECT_SEARCH_BY_FLOW_ID } from 'automation-ui-management/queries/crmObjectQueries';
import getIn from 'immutable-less/transmute/getIn';
import TeamAssetAccessPanelWrapper from 'automation-ui-management/components/panels/TeamAssetAccessPanelWrapper';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import { AutomationPlatformFlowProperties } from 'automation-ui-management/constants/CrmWorkflowProperties';
import OpenMoveFlowModalButton from 'automation-ui-management/components/OpenMoveFlowModalButton';
import OpenCloneFlowModalButton from 'automation-ui-management/components/OpenCloneFlowModalButton';
import { FrameworkListingLibProvider } from 'framework-listing-lib';
import ListingLibMoveAssetsModal from 'framework-listing-lib/dialog/ListingLibMoveAssetsModal';
import { useContext, useEffect } from 'react';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import OpenDeleteFlowModalButton from 'automation-ui-management/components/OpenDeleteFlowModalButton';
import DeleteWorkflowModal from 'automation-ui-management/components/modals/DeleteWorkflowModal';
import { hiddenFiltersForFolder } from 'automation-ui-management/FrameworkListingTable/constants/tableFilters';
import { SelectCampaignModal } from 'automation-ui-canvas/components/modals/SelectCampaignModal';
import { getSourceApp } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { useDispatch } from 'react-redux';
import { setModalStateById } from 'automation-ui-canvas/actions/MenuBarActions';
import { ASSIGN_TEAMS_ACCESS, BUSINESS_UNITS_ACCESS } from 'automation-ui-canvas/constants/Scopes';
import { OptinContext } from 'automation-ui-core/context/OptinContext';
import { jsx as _jsx } from "react/jsx-runtime";
export const useNewOption = () => {
  const {
    isOptedIn = false
  } = useContext(OptinContext);
  return {
    id: 'new',
    getLabel: () => I18n.text('automation.editorMenuBar.options.new'),
    onClick: () => {
      window.open(getCreationUrl(isOptedIn), '_blank');
    },
    external: true
  };
};
export const CloneOption = {
  id: 'clone',
  ButtonComponent: props => /*#__PURE__*/_jsx(OpenCloneFlowModalButton, Object.assign({}, props, {
    flowId: props.flow.flowId,
    hasObjectTypeAccess: true,
    sourceApp: getSourceApp(props.flow),
    tooltipProps: {
      placement: 'right'
    },
    upgradePoint: "automation-workflow-limit"
  })),
  getLabel: () => I18n.text('automation.editorMenuBar.options.clone'),
  external: true,
  ModalComponent: ({
    flow,
    onClose
  }) => {
    return /*#__PURE__*/_jsx(CloneWorkflowModal, {
      flowId: flow.flowId,
      onClose: onClose,
      onSuccess: onClose,
      openInNewTab: true
    });
  }
};
export const ExportOption = {
  id: 'export',
  getLabel: () => I18n.text('automation.editorMenuBar.options.export'),
  ModalComponent: ({
    flow,
    onClose
  }) => {
    return /*#__PURE__*/_jsx(ExportWorkflowModal, {
      flow: flow,
      onConfirm: onClose,
      onReject: onClose
    });
  },
  isQuickAction: true
};
export const AboutOption = {
  id: 'about',
  getLabel: () => I18n.text('automation.editorMenuBar.options.about'),
  ModalComponent: ({
    flow,
    onClose,
    modalState
  }) => {
    const {
      hasFeatureAccess: hasEditNameAccess
    } = useFeaturePermission({
      feature: EDIT_ACTIONS
    });
    const location = useLocation();
    const dispatch = useDispatch();
    const description = modalState === null || modalState === void 0 ? void 0 : modalState.description;
    const name = modalState === null || modalState === void 0 ? void 0 : modalState.name;
    const isRevisionPreview = !isNaN(parseInt(new URLSearchParams(location.search).get('revisionId') || '', 10));
    useEffect(() => {
      /* clear out staged description on unmount */
      return () => {
        dispatch(setModalStateById({
          menuOptionId: 'about',
          modalState: {}
        }));
      };
    }, [dispatch]);
    return /*#__PURE__*/_jsx(EditWorkflowNameAndDescriptionModal, {
      headerLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.editorMenuBar.options.about"
      }),
      onClose: onClose,
      flow: flow.toHybridFlowInterface(),
      flowId: flow.flowId,
      readOnly: Boolean(!hasEditNameAccess || isRevisionPreview || flow.getActionById(NEW_ACTION)),
      initialGeneratedDescription: description,
      initialGeneratedName: name
    });
  },
  isQuickAction: true
};
export const AssignTeamOption = {
  id: 'assignTeam',
  ButtonComponent: props => /*#__PURE__*/_jsx(OpenAssignTeamPanelButton, Object.assign({}, props, {
    flowId: props.flow.flowId,
    sourceApp: WORKFLOWS_APP
  })),
  getLabel: () => I18n.text('automation.editorMenuBar.options.assignTeam'),
  getIsVisible: ({
    getHasScope
  }) => {
    return getHasScope(ASSIGN_TEAMS_ACCESS);
  },
  ModalComponent: ({
    flow,
    onClose
  }) => {
    const {
      loading: loadingCrmObject,
      error: errorCrmObject,
      data: flowCrmObjectByIdData
    } = useQuery(CRM_OBJECT_SEARCH_BY_FLOW_ID, {
      variables: {
        flowId: String(flow.flowId)
      }
    });
    const client = useDataFetchingClient();
    const crmObject = getIn([CRM_OBJECT_SEARCH_FIELD_NAME], flowCrmObjectByIdData);
    if (loadingCrmObject || errorCrmObject || !crmObject) {
      return null;
    }
    const teamIds = parseMultiEnumValue(getPropertyValue(crmObject, AutomationPlatformFlowProperties.TEAM_IDS)).map(val => Number(val));
    return /*#__PURE__*/_jsx(TeamAssetAccessPanelWrapper, {
      assignedTeamIds: teamIds,
      assetTypeDisplayName: flow.name || '',
      flowId: flow.flowId,
      onClosePanel: onClose,
      onSuccess: () => {
        client.cache.evict({
          fieldName: CRM_OBJECT_SEARCH_FIELD_NAME
        });
      }
    });
  },
  isQuickAction: true
};
export const AssignBusinessUnitOption = {
  id: 'assignBusinessUnit',
  ButtonComponent: props => /*#__PURE__*/_jsx(OpenBusinessUnitModalButton, Object.assign({}, props, {
    flowId: props.flow.flowId,
    sourceApp: WORKFLOWS_APP,
    hasCurrentBusinessUnit: false
  })),
  getLabel: () => I18n.text('automation.editorMenuBar.options.assignBusinessUnit_buv2'),
  getIsVisible: ({
    getHasScope
  }) => {
    return getHasScope(BUSINESS_UNITS_ACCESS);
  },
  ModalComponent: ({
    flow,
    onClose
  }) => {
    const client = useDataFetchingClient();
    const {
      data: flowCrmObjectByIdData
    } = useQuery(CRM_OBJECT_SEARCH_BY_FLOW_ID, {
      variables: {
        flowId: String(flow.flowId)
      }
    });
    const crmObject = getIn([CRM_OBJECT_SEARCH_FIELD_NAME], flowCrmObjectByIdData);
    if (!crmObject) {
      return null;
    }
    return /*#__PURE__*/_jsx(AssignBusinessUnitModal, {
      crmObject: crmObject,
      onClose: onClose,
      onSuccess: () => {
        client.cache.evict({
          fieldName: CRM_OBJECT_SEARCH_FIELD_NAME
        });
      }
    });
  },
  isQuickAction: true
};
export const AssignCampaignOption = {
  id: 'campaign',
  getLabel: () => I18n.text('automation.editorMenuBar.options.campaign.label'),
  getIsVisible: ({
    isClassicWorkflow
  }) => isClassicWorkflow,
  ModalComponent: SelectCampaignModal
};
export const MoveOption = {
  id: 'move',
  ButtonComponent: props => {
    return /*#__PURE__*/_jsx(OpenMoveFlowModalButton, Object.assign({}, props, {
      flowId: props.flow.flowId,
      sourceApp: WORKFLOWS_APP
    }));
  },
  getLabel: () => I18n.text('automation.editorMenuBar.options.move'),
  ModalComponent: ({
    flow,
    onClose
  }) => {
    const {
      usageTracker
    } = useContext(UsageTrackerContext);
    const client = useDataFetchingClient();
    const {
      data: flowCrmObjectByIdData
    } = useQuery(CRM_OBJECT_SEARCH_BY_FLOW_ID, {
      variables: {
        flowId: String(flow.flowId)
      }
    });
    const crmObject = getIn([CRM_OBJECT_SEARCH_FIELD_NAME], flowCrmObjectByIdData);
    if (!crmObject) {
      return null;
    }
    return /*#__PURE__*/_jsx(FrameworkListingLibProvider, {
      objectType: AUTOMATION_PLATFORM_FLOW_ID,
      trackerProps: {
        applicationId: 'automation-ui-canvas',
        tracker: usageTracker
      },
      folderProps: {
        hiddenFilter: hiddenFiltersForFolder
      },
      children: /*#__PURE__*/_jsx(ListingLibMoveAssetsModal, {
        selectedObjectOrObjects: crmObject,
        onClose: onClose,
        onSuccess: () => {
          client.cache.evict({
            fieldName: CRM_OBJECT_SEARCH_FIELD_NAME
          });
        }
      })
    });
  },
  isQuickAction: true
};
export const DeleteOption = {
  id: 'delete',
  ButtonComponent: props => {
    return /*#__PURE__*/_jsx(OpenDeleteFlowModalButton, Object.assign({}, props, {
      flowId: props.flow.flowId,
      sourceApp: WORKFLOWS_APP
    }));
  },
  getLabel: () => I18n.text('automation.editorMenuBar.options.delete'),
  ModalComponent: ({
    flow,
    onClose
  }) => {
    return /*#__PURE__*/_jsx(DeleteWorkflowModal, {
      flowId: flow.flowId,
      handleDismissModal: onClose
    });
  }
};
const OrganizeOption = {
  id: 'organize',
  getLabel: () => I18n.text('automation.editorMenuBar.options.organize'),
  options: [[AboutOption, AssignTeamOption, AssignBusinessUnitOption, AssignCampaignOption, MoveOption]]
};
export const useFileOption = props => {
  const NewOption = useNewOption(props);
  return {
    id: 'file',
    getLabel: () => I18n.text('automation.editorMenuBar.menus.file'),
    options: [[NewOption, CloneOption, ExportOption, OrganizeOption], [DeleteOption]]
  };
};
export const ReviewFileOption = {
  id: 'file',
  getLabel: () => I18n.text('automation.editorMenuBar.menus.file'),
  options: [[ExportOption]]
};